// KPI aziendali — helper condivisi: card KPI con formula a tocco, gruppo, griglia, classifica, formati
import React, { useState } from "react";
import { Info } from "lucide-react";
import { Card } from "../ui/card";
import { Delta, SourceNote } from "../bi/biShared";

export const fmtPct = (v, d = 1) => (v == null ? "—" : `${v.toLocaleString("it-IT", { maximumFractionDigits: d })}%`);
export const fmtNum = (v, d = 1) => (v == null ? "—" : v.toLocaleString("it-IT", { maximumFractionDigits: d }));
export const fmtDays = (v) => (v == null ? "—" : `${fmtNum(v, 1)} gg`);

export const Kpi = ({ label, value, sub, delta, invert = false, formula, testId, accent = false }) => {
  const [open, setOpen] = useState(false);
  const Tag = formula ? "button" : "div";
  return (
    <Tag type={formula ? "button" : undefined} onClick={formula ? () => setOpen((o) => !o) : undefined} data-testid={testId}
      className={`bg-white border rounded-2xl p-3.5 flex flex-col gap-1 min-h-[104px] text-left w-full transition-colors ${open ? "border-[#8A1C2A]/50" : "border-stone-200"} ${formula ? "hover:bg-stone-50/70" : ""}`}>
      <div className="flex items-start justify-between gap-1">
        <p className="text-[11px] uppercase tracking-wide text-stone-500 font-semibold leading-tight">{label}</p>
        {formula && <Info className={`w-3.5 h-3.5 shrink-0 ${open ? "text-[#8A1C2A]" : "text-stone-300"}`} />}
      </div>
      <p className={`text-lg sm:text-2xl font-bold tabular-nums leading-tight ${accent ? "text-[#8A1C2A]" : "text-stone-900"}`} data-testid={`${testId}-value`}>{value ?? "—"}</p>
      {(delta !== undefined || sub) && (
        <div className="mt-auto flex items-center justify-between gap-1 flex-wrap">
          {delta !== undefined && <Delta value={delta} suffix="vs prec." invert={invert} />}
          {sub && <span className="text-[11px] text-stone-500 leading-tight">{sub}</span>}
        </div>
      )}
      {open && formula && <p className="text-[11px] leading-snug text-stone-700 bg-stone-50 border border-stone-200 rounded-lg p-2 mt-1" data-testid={`${testId}-formula`}><b className="text-[#8A1C2A]">Formula:</b> {formula}</p>}
    </Tag>
  );
};

export const Group = ({ num, title, icon: Icon, note, children, testId }) => (
  <Card className="bg-stone-50/60 border-stone-200 p-3 sm:p-4 space-y-3" data-testid={testId}>
    <div className="flex items-center justify-between gap-2">
      <h3 className="font-semibold text-stone-900 flex items-center gap-2 text-sm sm:text-base">
        <span className="w-6 h-6 rounded-md bg-[#8A1C2A] text-white text-[11px] font-bold inline-flex items-center justify-center shrink-0">{num}</span>
        {Icon && <Icon className="w-4 h-4 text-[#8A1C2A] shrink-0" />} {title}
      </h3>
      {note && <SourceNote text={note} testId={`${testId}-source`} />}
    </div>
    {children}
  </Card>
);

export const KpiGrid = ({ children, cols = 4 }) => <div className={`grid grid-cols-2 ${cols === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"} gap-2.5`}>{children}</div>;

export const SubTitle = ({ children }) => <p className="text-xs font-semibold text-stone-700 uppercase tracking-wide pt-1">{children}</p>;

export const RankList = ({ rows, label, value, sub, right, emptyLabel = "Nessun dato nel periodo", testIdPrefix }) => {
  if (!rows?.length) return <p className="text-sm text-stone-500 italic py-6 text-center" data-testid={`${testIdPrefix}-empty`}>{emptyLabel}</p>;
  return (
    <div className="divide-y divide-stone-100 bg-white rounded-xl border border-stone-200" data-testid={`${testIdPrefix}-list`}>
      {rows.map((r, i) => (
        <div key={r.key || r.code || i} className="flex items-center gap-3 px-3 py-2.5 min-h-[52px]" data-testid={`${testIdPrefix}-${i}`}>
          <span className="w-5 text-[11px] text-stone-400 font-semibold shrink-0 tabular-nums">{i + 1}</span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-stone-900 truncate">{label(r)}</p>
            {sub && <p className="text-[11px] text-stone-500 truncate">{sub(r)}</p>}
          </div>
          <div className="text-right shrink-0 max-w-[45%]">
            <p className="text-sm font-bold tabular-nums text-stone-900">{value(r)}</p>
            {right && <p className="text-[11px] text-stone-500 tabular-nums truncate">{right(r)}</p>}
          </div>
        </div>
      ))}
    </div>
  );
};
